import React, { useState, useMemo, useEffect } from 'react';
import { BigNumber, ethers } from 'ethers';

import { Button, Select, MenuItem, InputLabel, Typography, withStyles } from '@material-ui/core';
// import Button from '../../../components/Button'
import Modal, { ModalProps } from '../../../components/Modal';
import ModalActions from '../../../components/ModalActions';
import ModalTitle from '../../../components/ModalTitle';
import TokenInput from '../../../components/TokenInput';
import styled from 'styled-components';

import { getDisplayBalance } from '../../../utils/formatBalance';
import Label from '../../../components/Label';
import useLpStats from '../../../hooks/useLpStats';
import useTokenBalance from '../../../hooks/useTokenBalance';
import useTombFinance from '../../../hooks/useTombFinance';
import { useAccount, useBalance } from 'wagmi';
import useApproveZapper, { ApprovalState } from '../../../hooks/useApproveZapper';
import { HOG_TICKER, GHOG_TICKER, OS_TICKER } from '../../../utils/constants';
import { Alert } from '@material-ui/lab';
import TokenSymbol from '../../../components/TokenSymbol';
import { useTransactionAdder } from '../../../state/transactions/hooks';

interface ZapProps extends ModalProps {
  onConfirm?: (zapAsset: string, lpName: string, amount: string) => void;
  tokenName?: string;
  decimals?: number;
  initialZapDirection?: 'in' | 'out';
}

const ModalWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
`;

const StyledModal = styled(Modal)`
  background: rgb(17 17 17 / 0.95);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 20px;
  width: 320px;
  color: white;
`;

const StyledSelect = styled(Select)`
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: white;
  width: 100%;
  margin: 4px 0;
  height: 40px;
  
  & .MuiSelect-icon {
    color: white;
  }
`;

const StyledMenuItem = withStyles({
  root: {
    backgroundColor: 'rgb(17 17 17 / 0.95)',
    color: 'white',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
    '&.Mui-selected': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
    }
  },
})(MenuItem);

const StyledButton = styled(Button)`
  background: linear-gradient(45deg, #102D3C, #214E81, #506179, #ED5409, #FFCB67);
  border-radius: 20px;
  color: white;
  width: 100%;
  padding: 10px;
  margin-top: 16px;
  
  &:hover {
    opacity: 0.9;
  }
`;

const RadioWrapper = styled.div<{ direction: 'in' | 'out' }>`
  &[data-state='checked'] {
    background-color: ${props => props.direction === 'in' ? 'rgba(110, 189, 112, 0.27)' : '#BD6EAC44'};
    border-color: ${props => props.direction === 'in' ? 'rgba(110, 189, 112, 0.4)' : '#BD6EAC66'};
  }
  &:hover {
    background-color: ${props => props.direction === 'in' ? 'rgba(71, 193, 75, 0.53)' : 'rgba(193, 82, 169, 0.53)'};
    border-color: ${props => props.direction === 'in' ? 'rgba(110, 189, 112, 0.53)' : '#BD6EAC88'};
  }
`;

const getAvailableTokens = (lpTokenName: string, direction: string) => {
  if (direction === 'in') {
    if (lpTokenName === 'HOG-OS-LP') {
      return [
        { symbol: HOG_TICKER, label: 'HOG' },
        { symbol: 'OS', label: 'OS' }
      ];
    } else if (lpTokenName === 'GHOG-OS-LP') {
      return [
        { symbol: GHOG_TICKER, label: 'GHOG' },
        { symbol: 'OS', label: 'OS' }
      ];
    }
  } else { // zap out
    if (lpTokenName === 'HOG-OS-LP') {
      return [
        { symbol: HOG_TICKER, label: 'HOG' }
      ];
    } else if (lpTokenName === 'GHOG-OS-LP') {
      return [
        { symbol: GHOG_TICKER, label: 'GHOG' }
      ];
    }
  }
  return [];
};

const StyledAlert = styled(Alert)`
  && {
    background-color: ${() => 
      localStorage.getItem('theme') === "theme-3" || localStorage.getItem('theme') === "theme-4" 
        ? '#BD6EAC44' 
        : '#BD6EAC'
    };
    border: ${() => 
      localStorage.getItem('theme') === "theme-3" || localStorage.getItem('theme') === "theme-4" 
        ? '2px solid #BD6EAC66' 
        : '0'
    };
    padding: 6px 12px;
    font-size: 12px;
    transition: all 0.2s;
  }
`;

const ZapModal: React.FC<ZapProps> = ({ 
  onConfirm = async () => {},
  onDismiss, 
  tokenName = '', 
  decimals = 18,
  initialZapDirection = 'in'
}) => {
  const tombFinance = useTombFinance();
  const { address } = useAccount();
  const { data: ftmBalanceData } = useBalance({
    address,
  });
  const ftmBalance = ftmBalanceData ? ftmBalanceData.formatted : '0';
  const tombBalance = useTokenBalance(tombFinance.HOG);
  const tshareBalance = useTokenBalance(tombFinance.GHOG);
  const [val, setVal] = useState('');
  const [zappingToken, setZappingToken] = useState(HOG_TICKER);
  const [zappingTokenBalance, setZappingTokenBalance] = useState(ftmBalance);
  const [estimate, setEstimate] = useState({ token0: '0', token1: '0' }); // token0 will always be S in this case
  const [zapDirection, setZapDirection] = useState(initialZapDirection);
  const osBalance = useTokenBalance(tombFinance.OS);
  const [isApproving, setIsApproving] = useState(false);
  const [isZapping, setIsZapping] = useState(false);
  const addTransaction = useTransactionAdder();

  // Update the LP balance hooks to use the correct token names
  const hogOsLpBalance = useTokenBalance(
    tokenName === 'HOG-OS-LP' ? tombFinance.HOG_OS_LP : null
  );
  const ghogOsLpBalance = useTokenBalance(
    tokenName === 'GHOG-OS-LP' ? tombFinance.GHOG_OS_LP : null
  );

  // Create a memoized token to approve based on direction
  const tokenToApprove = useMemo(() => {
    return zapDirection === 'out' ? tokenName : zappingToken;
  }, [zapDirection, tokenName, zappingToken]);

  // Update the hook to use the memoized token
  const [approveStatus, approve, currentAllowance] = useApproveZapper(
    tokenToApprove,
    tokenName,
    zapDirection // Add zapDirection as a parameter
  );

  const tombFtmLpStats = useLpStats('HOG-OS-LP');
  const tShareFtmLpStats = useLpStats('GHOG-OS-LP');
  const tombLPStats = useMemo(() => (tombFtmLpStats ? tombFtmLpStats : null), [tombFtmLpStats]);
  const tshareLPStats = useMemo(() => (tShareFtmLpStats ? tShareFtmLpStats : null), [tShareFtmLpStats]);
  const ftmAmountPerLP = tokenName.startsWith(HOG_TICKER) ? tombLPStats?.ftmAmount : tshareLPStats?.ftmAmount;

  // Add check for if more approval is needed
  const needsApproval = useMemo(() => {
    if (!currentAllowance || !val) return false;
    const bigAmount = ethers.utils.parseUnits(val, decimals);
    return currentAllowance.lt(bigAmount);
  }, [currentAllowance, val, decimals]);

  // Add approve handler
  const handleApprove = async () => {
    try {
      setIsApproving(true);
      await approve(val);
    } catch (error) {
      console.error('Approval error:', error);
    } finally {
      setIsApproving(false);
    }
  };

  /**
   * Checks if a value is a valid number or not
   * @param n is the value to be evaluated for a number
   * @returns
   */
  function isNumeric(n: any) {
    return !isNaN(parseFloat(n)) && isFinite(n);
  }
  const handleChangeAsset = (event: any) => {
    if (zapDirection === 'out') {
      // Don't allow token changes during zap out
      return;
    }
    
    const value = event.target.value;
    setZappingToken(value);
    
    // Update balance based on token
    if (value === GHOG_TICKER) {
      setZappingTokenBalance(getDisplayBalance(tshareBalance, decimals));
    } else if (value === HOG_TICKER) {
      setZappingTokenBalance(getDisplayBalance(tombBalance, decimals));
    } else if (value === 'OS') {
      setZappingTokenBalance(getDisplayBalance(osBalance, decimals));
    }
  };

  const handleChange = async (e: any) => {
    if (e.currentTarget.value === '' || e.currentTarget.value === 0) {
      setVal(e.currentTarget.value);
      setEstimate({ token0: '0', token1: '0' });
        return;
    }
    
    if (!isNumeric(e.currentTarget.value)) return;
    
    setVal(e.currentTarget.value);
    try {
        const estimateZap = await tombFinance.estimateZapIn(
            zappingToken, 
            tokenName, 
            String(e.currentTarget.value)
        );
        
        // Safely handle the estimate values
        const token0Value = estimateZap && estimateZap[0] ? estimateZap[0].toString() : '0';
        const token1Value = estimateZap && estimateZap[1] ? estimateZap[1].toString() : '0';
        
        setEstimate({ 
            token0: token0Value, 
            token1: token1Value 
        });
    } catch (error) {
        console.error('Error getting zap estimate:', error);
        setEstimate({ token0: '0', token1: '0' });
    }
  };

  // Update useEffect to handle LP balances correctly
  useEffect(() => {
    if (zapDirection === 'out') {
      // For zap out, we need to use LP token balance and approval
      if (tokenName === 'HOG-OS-LP') {
        setZappingToken(HOG_TICKER); // Output token is fixed to HOG
        setZappingTokenBalance(getDisplayBalance(hogOsLpBalance, decimals)); // But balance is LP token
      } else if (tokenName === 'GHOG-OS-LP') {
        setZappingToken(GHOG_TICKER); // Output token is fixed to GHOG
        setZappingTokenBalance(getDisplayBalance(ghogOsLpBalance, decimals)); // But balance is LP token
      }
    } else {
      // For zap in, we use the selected input token's balance
      const availableTokens = getAvailableTokens(tokenName, zapDirection);
      if (availableTokens.length > 0) {
        const initialToken = availableTokens[0].symbol;
        setZappingToken(initialToken);
        
        // Set balance based on the input token
        if (initialToken === GHOG_TICKER) {
          setZappingTokenBalance(getDisplayBalance(tshareBalance, decimals));
        } else if (initialToken === HOG_TICKER) {
          setZappingTokenBalance(getDisplayBalance(tombBalance, decimals));
        } else if (initialToken === 'OS') {
          setZappingTokenBalance(getDisplayBalance(osBalance, decimals));
        }
      }
    }
  }, [
    tokenName, 
    zapDirection, 
    tshareBalance, 
    tombBalance, 
    osBalance, 
    hogOsLpBalance, 
    ghogOsLpBalance, 
    decimals
  ]);

  // Update handleSelectMax to use the correct LP token names
  const handleSelectMax = async () => {
    let maxBalance;
    if (zapDirection === 'out') {
      if (tokenName === 'HOG-OS-LP') {
        maxBalance = getDisplayBalance(hogOsLpBalance, decimals);
      } else if (tokenName === 'GHOG-OS-LP') {
        maxBalance = getDisplayBalance(ghogOsLpBalance, decimals);
      }
    } else {
      maxBalance = zappingTokenBalance;
    }
    
    if (maxBalance) {
      setVal(maxBalance);
      console.log('Setting max balance:', maxBalance, 'for token:', tokenName);
    }
  };

  // Add a safe handler for the zap action
  const handleZap = async () => {
    if (!val || !tokenName) {
        console.error('Missing required zap parameters');
        return;
    }

    try {
        setIsZapping(true);
        let tx;
        
        if (zapDirection === 'in') {
            tx = await tombFinance.zapIn(zappingToken, tokenName, val);
        } else {
            tx = await tombFinance.zapOut(tokenName, val);
        }
        
        // Add the transaction to history
        addTransaction(tx, {
            summary: zapDirection === 'in'
                ? `Zap in ${val} ${zappingToken} to ${tokenName}`
                : `Zap out ${val} ${tokenName} to ${tokenName.startsWith('HOG') ? 'HOG' : 'GHOG'}`,
        });

        if (onDismiss) {
            onDismiss();
        }
    } catch (error) {
        console.error('Error during zap:', error);
    } finally {
        setIsZapping(false);
    }
  };

  return (
    <Modal>
      <ModalTitle text={`${tokenName} Zapper`} />
      
      {/* Swap the order of Zap In and Zap Out */}
      <div className="w-full flex items-center justify-center mt-4 mb-6">
        <button
          className="flex-1 flex justify-end"
          onClick={() => setZapDirection('out')}
        >
          <p className={`font-urbanist text-m-body-large leading-relaxed font-semibold transition cursor-pointer active:scale-[0.99] active:translate-y-[1px] ${
            zapDirection === 'out' 
              ? 'text-white' 
              : 'text-white/40'
          }`}>
            Zap Out
          </p>
        </button>
        <button
          type="button"
          role="switch"
          aria-checked={zapDirection === 'in'}
          data-state={zapDirection === 'in' ? 'checked' : 'unchecked'}
          value={zapDirection}
          onClick={() => setZapDirection(prev => prev === 'in' ? 'out' : 'in')}
          className="peer inline-flex shrink-0 cursor-pointer items-center rounded-full border-2 border-white/20 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 h-7 w-14 mx-4 bg-transparent"
        >
          <span
            data-state={zapDirection === 'in' ? 'checked' : 'unchecked'}
            className="pointer-events-none block rounded-full bg-white shadow-lg ring-0 transition-transform h-6 w-6 data-[state=checked]:translate-x-7 data-[state=unchecked]:translate-x-0"
          ></span>
        </button>
        <button
          className="flex-1 flex justify-start"
          onClick={() => setZapDirection('in')}
        >
          <p className={`font-urbanist text-m-body-large leading-relaxed font-semibold transition cursor-pointer active:scale-[0.99] active:translate-y-[1px] ${
            zapDirection === 'in' 
              ? 'text-white' 
              : 'text-white/40'
          }`}>
            Zap In
          </p>
        </button>
      </div>

      <InputLabel style={{ color: 'white', marginTop: '12px', fontSize: '14px' }}>
        Select asset to zap {zapDirection}
      </InputLabel>
      <StyledSelect
        onChange={handleChangeAsset}
        value={zappingToken}
        disabled={zapDirection === 'out'}
      >
        {getAvailableTokens(tokenName, zapDirection).map((token) => (
          <StyledMenuItem key={token.symbol} value={token.symbol}>
            <div className="flex items-center gap-2">
              <TokenSymbol symbol={token.symbol} size={24} />
              <span>{token.label}</span>
            </div>
          </StyledMenuItem>
        ))}
      </StyledSelect>
      <TokenInput
        onSelectMax={handleSelectMax}
        onChange={handleChange}
        value={val}
        max={zappingTokenBalance}
        symbol={zapDirection === 'out' ? tokenName : zappingToken}
      />
      {/* <Label text="Zap Estimations" />
      <StyledDescriptionText>
        {tokenName}: {Number(estimate.token0) / Number(ftmAmountPerLP)}
      </StyledDescriptionText>
      <StyledDescriptionText>
        ({Number(estimate.token0)} {OS_TICKER} / {Number(estimate.token1)}{' '}
        {tokenName.startsWith(HOG_TICKER) ? HOG_TICKER : GHOG_TICKER})
      </StyledDescriptionText> */}
      
      {needsApproval && (
        <div className="text-sm text-red-500 mt-2">
          Additional approval needed for this amount
        </div>
      )}
      
      <ModalActions>
        <button
          onClick={needsApproval ? handleApprove : handleZap}
          disabled={isApproving || isZapping}
          className='tracking-normal inline-flex whitespace-nowrap backdrop-blur items-center justify-center gap-x-1 py-2 px-6 rounded-full font-bold focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 [&>svg]:stroke-[3px] bg-button/[0.16] hover:bg-button/[0.24] transition focus-visible:ring-button text-body'
        >
          {needsApproval 
            ? (isApproving ? 'Approving...' : 'Approve More') 
            : (isZapping ? 'Zapping...' : `Zap ${zapDirection}`)}
        </button>
      </ModalActions>

      <StyledActionSpacer />
      <StyledAlert 
        variant="filled" 
        severity="warning"
        style={{
          color: '#BD6EAC66'
        }}
      >
        {zapDirection === 'out' 
          ? `You can only zap out your ${tokenName} to ${tokenName === 'HOG-OS-LP' ? 'HOG' : 'GHOG'}.`
          : `Unused ${tokenName === 'HOG-OS-LP' ? 'HOG' : 'GHOG'}/OS tokens will be sent back to your wallet.`
        }
      </StyledAlert>
    </Modal>
  );
};

const StyledActionSpacer = styled.div`
  height: 12px;
  width: 100%;
`;

const StyledDescriptionText = styled.div`
  align-items: center;
  color: ${(props) => props.theme.color.grey[400]};
  display: flex;
  font-size: 14px;
  font-weight: 700;
  height: 22px;
  justify-content: flex-start;
`;

export default ZapModal;
